import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { servicesSubbrokerLoginURL, servicesSubbrokerOtpSendURL, servicesSubbrokerOtpVerifyURL } from '../utils/Urls'
import './LocalSSOLogin.css'

export default function SubbrokerLogin() {
  const [mobileNumber, setMobileNumber] = useState('')
  const [loginMode, setLoginMode] = useState('otp')
  const [passwordCode, setPasswordCode] = useState('')
  const [password, setPassword] = useState('')
  const [otp, setOtp] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [challengeId, setChallengeId] = useState('')
  const [subbrokerCode, setSubbrokerCode] = useState('')
  const [resendIn, setResendIn] = useState(0)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const root = document.getElementById('root')
    if (root) root.classList.add('login-route-root')
    return () => {
      if (root) root.classList.remove('login-route-root')
    }
  }, [])

  useEffect(() => {
    if (resendIn <= 0) return undefined
    const timer = window.setInterval(() => setResendIn((value) => Math.max(0, value - 1)), 1000)
    return () => window.clearInterval(timer)
  }, [resendIn])

  const sendOtp = async (event) => {
    event.preventDefault()
    const mobile = mobileNumber.replace(/\D/g, '')
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      setError('Enter a valid 10-digit registered mobile number.')
      return
    }
    setError('')
    setIsLoading(true)
    try {
      const response = await axios.post(servicesSubbrokerOtpSendURL, { mobile_number: mobile }, { withCredentials: true })
      if (response?.data?.status !== 'success' || !response?.data?.challenge_id) throw new Error('Unexpected OTP response')
      setChallengeId(response.data.challenge_id)
      setSubbrokerCode(response?.data?.user?.subbroker_code || '')
      setResendIn(60)
    } catch (sendError) {
      const status = sendError?.response?.status
      setError(status === 404 ? 'This mobile number is not registered for an active Subbroker.' : status === 429 ? 'Too many attempts. Please try again later.' : 'Unable to send OTP right now. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const verifyOtp = async (event) => {
    event.preventDefault()
    if (!/^\d{6}$/.test(otp)) { setError('Enter the 6-digit OTP.'); return }
    setError('')
    setIsLoading(true)
    try {
      const response = await axios.post(servicesSubbrokerOtpVerifyURL, {
        mobile_number: mobileNumber.replace(/\D/g, ''), challenge_id: challengeId, otp,
      }, { withCredentials: true })
      const code = response?.data?.user?.subbroker_code
      if (response?.data?.status !== 'success' || !code) {
        throw new Error('Unexpected login response')
      }
      localStorage.setItem('subbroker_code', code)
      window.location.assign('/')
    } catch (verifyError) {
      const status = verifyError?.response?.status
      setError(status === 401 ? 'The OTP is invalid or expired.' : status === 429 ? 'Too many attempts. Please try again later.' : 'Unable to sign in right now. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const loginWithPassword = async (event) => {
    event.preventDefault()
    if (!passwordCode.trim() || !password) {
      setError('Enter your broker code and password.')
      return
    }
    setError('')
    setIsLoading(true)
    try {
      const response = await axios.post(servicesSubbrokerLoginURL, {
        subbroker_code: passwordCode.trim().toUpperCase(), password,
      }, { withCredentials: true })
      const code = response?.data?.user?.subbroker_code
      if (response?.data?.status !== 'success' || !code) throw new Error('Unexpected login response')
      localStorage.setItem('subbroker_code', code)
      window.location.assign('/')
    } catch (loginError) {
      const status = loginError?.response?.status
      const serverDetail = loginError?.response?.data?.detail
      setError(status === 429 ? 'Too many attempts. Please try again later.' : serverDetail || 'Unable to sign in right now. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const changeMode = (mode) => {
    setLoginMode(mode)
    setChallengeId('')
    setSubbrokerCode('')
    setOtp('')
    setError('')
  }

  return (
    <main className="login-page">
      <section className="login-hero" aria-label="About the Subbroker platform">
        <div className="login-orb login-orb-one" />
        <div className="login-orb login-orb-two" />

        <div className="login-hero-copy">
          <span className="login-eyebrow">PARTNER PORTAL</span>
          <h1>Your business.<br />One clear view.</h1>
          <p>Track clients, monitor growth and manage your subbroker business from one secure workspace.</p>

          <div className="login-benefits">
            <div><span>01</span><p><strong>Stay informed</strong>Live business insights</p></div>
            <div><span>02</span><p><strong>Move faster</strong>Everything in one place</p></div>
            <div><span>03</span><p><strong>Stay secure</strong>Protected partner access</p></div>
          </div>
        </div>

        <p className="login-hero-footer">Trusted tools for every stage of your growth.</p>
      </section>

      <section className="login-panel">
        <div className="login-brand">
          <img className="login-brand-logo" src={`${process.env.PUBLIC_URL || ''}/ats-logo.png`} alt="Aditya Trading Solutions" />
        </div>

        <div className="login-card">
          <span className="login-status"><i /> SECURE SIGN IN</span>
          <h2>Welcome back</h2>
          {loginMode === 'otp' && challengeId && <div className="subbroker-code-card subbroker-code-heading" aria-live="polite">
            <span>Subbroker code</span><strong>{subbrokerCode}</strong>
          </div>}
          <p className={`login-intro ${challengeId ? 'login-intro-compact' : ''}`}>{loginMode === 'password' ? 'Sign in with your broker code and password.' : challengeId ? 'Enter the OTP sent to your registered mobile.' : 'Sign in with your registered mobile number.'}</p>

          {!challengeId && <div className="login-method-tabs" role="tablist" aria-label="Sign-in method">
            <button type="button" className={loginMode === 'otp' ? 'active' : ''} onClick={() => changeMode('otp')}>Mobile OTP</button>
            <button type="button" className={loginMode === 'password' ? 'active' : ''} onClick={() => changeMode('password')}>Code &amp; password</button>
          </div>}

          {loginMode === 'otp' ? <form className="login-form" onSubmit={challengeId ? verifyOtp : sendOtp}>
            <input
              id="mobile-number"
              name="mobileNumber"
              type="tel"
              inputMode="numeric"
              aria-label="Mobile number"
              placeholder="Mobile number"
              value={mobileNumber}
              onChange={(event) => setMobileNumber(event.target.value.replace(/\D/g, '').slice(0, 10))}
              autoComplete="tel"
              maxLength="10"
              disabled={isLoading || Boolean(challengeId)}
            />

            {challengeId && <>
              <label htmlFor="subbroker-otp">One-time password</label>
              <input id="subbroker-otp" name="otp" type="text" inputMode="numeric" value={otp}
                onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))}
                autoComplete="one-time-code" maxLength="6" disabled={isLoading} autoFocus />
            </>}

            {error && <div className="login-error" role="alert">{error}</div>}

            <div className={challengeId ? 'login-otp-actions' : ''}>
            <button type="submit" disabled={isLoading} className="login-button">
              <span>{isLoading ? 'Please wait…' : challengeId ? 'Verify OTP' : 'Send OTP'}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {challengeId && <button type="button" className="login-button login-resend-button" disabled={isLoading || resendIn > 0} onClick={sendOtp}>
              <span>{resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend OTP'}</span>
            </button>}
            </div>
            {challengeId && <button type="button" className="login-change-mobile" disabled={isLoading} onClick={() => { setChallengeId(''); setSubbrokerCode(''); setOtp(''); setMobileNumber(''); setResendIn(0); setError('') }}>
              Change mobile number
            </button>}
          </form> : <form className="login-form" onSubmit={loginWithPassword}>
            <input id="broker-code" name="brokerCode" type="text" value={passwordCode}
              onChange={(event) => setPasswordCode(event.target.value.toUpperCase())}
              aria-label="Broker code" placeholder="Broker code"
              autoComplete="username" maxLength="32" disabled={isLoading} />
            <div className="login-password-field">
              <input id="broker-password" name="password" type={showPassword ? 'text' : 'password'} value={password}
                onChange={(event) => setPassword(event.target.value)}
                aria-label="Password" placeholder="Password"
                autoComplete="current-password" maxLength="128" disabled={isLoading} />
              <button type="button" className="login-password-toggle" tabIndex={-1}
                onClick={() => setShowPassword((value) => !value)} disabled={isLoading}
                aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword}>
                {showPassword ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a20.4 20.4 0 015.06-5.94M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a20.4 20.4 0 01-2.34 3.14M14.12 14.12a3 3 0 11-4.24-4.24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth="1.7" />
                  </svg>
                )}
              </button>
            </div>
            {error && <div className="login-error" role="alert">{error}</div>}
            <button type="submit" disabled={isLoading} className="login-button">
              <span>{isLoading ? 'Please wait…' : 'Sign in'}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </form>}

          <div className="login-security">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10V8a5 5 0 0110 0v2m-11 0h12v10H6V10z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
            <p><strong>Your access stays private</strong><span>The OTP expires in 5 minutes and can be used only once.</span></p>
          </div>

          <div className="login-signup">
            <span>New to ATS Partner Portal?</span>
            <a href="/signup">
              Sign up as a partner
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </a>
          </div>
        </div>

        <footer className="login-panel-footer">
          <span>© {new Date().getFullYear()} Aditya Trading Solutions</span>
        </footer>
      </section>
    </main>
  )
}
